import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Box, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import WarningDialog from '../../atoms/WarningDialog'
import useWarningDialog from '../../../hooks/useWarningDialog'

import ChoiceLabel from './ChoiceLabel'
import { normalizeBlindOffersRemaining } from './blindOffersLib'
const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'

const hasValue = (value) =>
  value !== null && value !== undefined && value !== ''

const getParsedCount = (value) => {
  if (!hasValue(value)) {
    return null
  }
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const getResolvedHasBlindPositiveOffer = ({
  hasBlindPositiveOffer,
  positiveCount,
  negativeCount,
  legacyCount,
}) => {
  if (typeof hasBlindPositiveOffer === 'boolean') {
    return hasBlindPositiveOffer
  }

  const parsedPositiveCount = getParsedCount(positiveCount)
  const parsedNegativeCount = getParsedCount(negativeCount)

  if (parsedPositiveCount !== null || parsedNegativeCount !== null) {
    return (parsedPositiveCount || 0) > 0
  }

  const parsedLegacyCount = getParsedCount(legacyCount)
  if (parsedLegacyCount !== null) {
    return parsedLegacyCount > 0
  }

  return null
}

const getHasBlindPositiveOffer = ({
  showBlindOption,
  selectedChoices,
  car,
  showBlindPositiveSignal = true,
}) => {
  if (!showBlindOption || !showBlindPositiveSignal) {
    return false
  }

  const selectedChoicesHasBlindPositiveOffer = getResolvedHasBlindPositiveOffer(
    {
      hasBlindPositiveOffer: selectedChoices?.hasBlindPositiveOffer,
      positiveCount: selectedChoices?.blindPositiveCount,
      negativeCount: selectedChoices?.blindNegativeCount,
      legacyCount: selectedChoices?.blindCount ?? selectedChoices?.blindOffers,
    }
  )

  if (selectedChoicesHasBlindPositiveOffer !== null) {
    return selectedChoicesHasBlindPositiveOffer
  }

  return (
    getResolvedHasBlindPositiveOffer({
      hasBlindPositiveOffer: car?.hasBlindPositiveOffer,
      positiveCount: car?.blindPositiveOfferCount,
      negativeCount: car?.blindNegativeOfferCount,
      legacyCount: car?.blindOfferCount,
    }) || false
  )
}

const getPreferenceCount = ({ selectedChoices, car }) =>
  Number(
    selectedChoices?.first ??
      selectedChoices?.firstChoiceCount ??
      car?.firstChoiceCount
  )

const isExhaustedCount = (value) => {
  const parsedValue = getParsedCount(value)
  return parsedValue !== null && parsedValue <= 0
}

const areAllBlindCapsExhausted = (blindOffersRemaining) => {
  const normalizedBlindOffersRemaining =
    normalizeBlindOffersRemaining(blindOffersRemaining)

  return (
    isExhaustedCount(normalizedBlindOffersRemaining.positive) &&
    isExhaustedCount(normalizedBlindOffersRemaining.negative)
  )
}

export default function CarCardChoices({
  variant = VARIANT_LIST,
  car,
  selectedChoices,
  disabledChoices = {},
  selection = 'none',
  onSelectionChange = () => {},
  readOnly = false,
  showBlindOption = false,
  showBlindPositiveSignal = true,
  blindOffersRemaining,
}) {
  const { t } = useTranslation()
  const [localSelection, setLocalSelection] = useState(selection)
  const {
    openDialog,
    handleSubmitDialog,
    handleOpenDialog,
    handleCloseDialog,
  } = useWarningDialog(() => {})

  useEffect(() => {
    setLocalSelection(selection)
  }, [selection])

  const handleLocalChange = async (e) => {
    const nextSelection = e.target.value
    // INFO get feedback from backend, checking quota as from multiple sessions, before accepting and rendering option change
    const success = await onSelectionChange(car, nextSelection)
    if (success !== false) {
      setLocalSelection(nextSelection)
    } else {
      handleOpenDialog()
    }
  }

  const isDetails = variant === VARIANT_DETAILS

  const preferenceCount = getPreferenceCount({ selectedChoices, car })
  const showPreferenceCount = Number.isFinite(preferenceCount)

  const hasBlindPositiveOffer = getHasBlindPositiveOffer({
    showBlindOption,
    showBlindPositiveSignal,
    selectedChoices,
    car,
  })
  const isBlindChoiceExhausted = areAllBlindCapsExhausted(blindOffersRemaining)
  const disableSelectedChoice =
    (disabledChoices?.firstChoice && localSelection !== 'selected') || readOnly
  const disableBlindChoice =
    !showBlindOption || readOnly || isBlindChoiceExhausted
  const disableNoneChoice = readOnly

  return (
    <>
      <Stack
        alignItems="flex-start"
        sx={{ width: 1, minWidth: '15ch' }}
        data-testid="car-card-choices"
      >
        <RadioGroup
          name={`${car.plate}.radioGroup`}
          defaultValue="none"
          onChange={handleLocalChange}
        >
          <FormControlLabel
            disabled={disableNoneChoice}
            control={
              <Radio
                checked={localSelection === 'none'}
                id={`${car.plate}.none`}
                data-testid={`${car.plate}.none`}
                value="none"
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t('carsToBuy.choice.noneChoice')}
              />
            }
          />
          <Box pb={isDetails ? 0 : 1} />
          <FormControlLabel
            disabled={disableSelectedChoice}
            control={
              <Radio
                checked={localSelection === 'selected'}
                id={`${car.plate}.selected`}
                data-testid={`${car.plate}.selected`}
                value="selected"
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t('carsToBuy.choice.selected')}
                sublabel={
                  showPreferenceCount
                    ? t('carsToBuy.choice.selectedChoices', {
                        number: preferenceCount,
                      })
                    : null
                }
              />
            }
          />
          <Box pb={isDetails ? 0 : 1} />
          {showBlindOption && (
            <>
              <FormControlLabel
                disabled={disableBlindChoice}
                control={
                  <Radio
                    checked={localSelection === 'blind'}
                    id={`${car.plate}.blind`}
                    data-testid={`${car.plate}.blind`}
                    value="blind"
                  />
                }
                label={
                  <ChoiceLabel
                    variant={variant}
                    label={t('carsToBuy.choice.blind')}
                    sublabel={
                      hasBlindPositiveOffer
                        ? t('carsToBuy.choice.atLeastOnePartner')
                        : null
                    }
                  />
                }
              />
              <Box pb={isDetails ? 0 : 1} />
            </>
          )}
        </RadioGroup>
      </Stack>
      <WarningDialog
        title={t('common.error.notAllowedAction')}
        content={
          <>
            {t(
              'common.error.sessions.partnerChoiceForSelectedVehicleWouldHitQuota'
            )}
          </>
        }
        open={openDialog}
        handleClose={handleCloseDialog}
        handleSubmit={handleSubmitDialog}
        hideCancel={true}
        confirmLabel={t('common.actionHide')}
      />
    </>
  )
}

CarCardChoices.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  car: T.object.isRequired,
  selectedChoices: T.object,
  disabledChoices: T.object,
  selection: T.string,
  onSelectionChange: T.func,
  readOnly: T.bool,
  showBlindOption: T.bool,
  showBlindPositiveSignal: T.bool,
  blindOffersRemaining: T.oneOfType([
    T.number,
    T.shape({
      positive: T.number,
      negative: T.number,
    }),
  ]),
}
